if (!customElements.get('ap-campaign-slider')) {
  class APCampaignSlider extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-go]'));
      this.track = this.querySelector('.ap-campaign__slides');
      this.pauseButton = this.querySelector('[data-pause]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = this.dataset.autoplay !== 'true';
      this.inView = true;
      this.focused = false;
      this.interval = Math.max(5000, Number(this.dataset.interval) || 15000);
      const on = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal: this.controller.signal });
      if (this.slides.length < 2) return;
      this.querySelector('.ap-campaign__controls').hidden = false;
      on(this.querySelector('[data-previous]'), 'click', () => this.go(this.index - 1, true));
      on(this.querySelector('[data-next]'), 'click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => on(dot, 'click', () => this.go(i, true)));
      on(this.pauseButton, 'click', () => { this.paused = !this.paused; this.updatePlayback(); });
      on(this, 'focusin', (event) => { this.focused = event.target.matches?.(':focus-visible') === true; this.schedule(); });
      on(this, 'pointerdown', () => { this.focused = false; this.schedule(); });
      on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } });
      on(document, 'visibilitychange', () => this.schedule());
      on(window, 'pageshow', () => {
        if (window.Shopify?.designMode) return;
        this.paused = this.dataset.autoplay !== 'true';
        this.focused = false;
        this.go(0);
      });
      on(this.motion, 'change', () => this.updatePlayback());
      on(this, 'keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
      on(this, 'touchstart', (event) => { if (event.touches.length === 1) this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY }; }, { passive: true });
      on(this, 'touchend', (event) => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      on(this, 'touchcancel', () => { this.touch = null; }, { passive: true });
      on(document, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.go(index, true);
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting; this.schedule(); });
        this.observer.observe(this);
      }
      this.go(0);
    }
    go(index, userInitiated = false) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, i) => { if (i === this.index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      this.updatePlayback();
    }
    updatePlayback() {
      const paused = this.paused || Boolean(window.Shopify?.designMode);
      this.pauseButton.hidden = false;
      this.pauseButton.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.pauseButton.querySelector('[data-pause-symbol]').textContent = paused ? '▶' : 'Ⅱ';
      this.track.setAttribute('aria-live', paused ? 'polite' : 'off');
      this.schedule();
    }
    schedule() {
      window.clearTimeout(this.timer);
      if (this.slides.length < 2 || this.paused || this.focused || !this.inView || document.hidden || window.Shopify?.designMode) return;
      this.timer = window.setTimeout(() => this.go(this.index + 1), this.interval);
    }
    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('ap-campaign-slider', APCampaignSlider);
}
